// ============================================================
// Dashboard API Service
// Connects to /api/v1/dashboard endpoints
// ============================================================

import { httpClient, ApiError, getApiBaseUrl, getAuthToken } from './http-client';

/** Range for dashboard stats: today (Jakarta), yesterday, or last N days */
export type DashboardStatsRange = 'today' | 'yesterday' | '7' | '14' | '30' | '90';

export interface DashboardStats {
  conversations_count: number;
  conversations_24h?: number;
  ai_handled: number;
  agent_handled: number;
  total_messages: number;
  /** AI tokens used in the selected range (from messages) */
  ai_tokens_used?: number;
  /** Echo of requested range */
  range?: string;
}

export interface BillingData {
  main_balance: number;
  transactions: BillingTransaction[];
}

export interface BillingTypeAmount {
  type: string;
  amount: number;
}

export interface BillingInvoice {
  id: string;
  reference?: string;
  description: string;
  amount: number;
  status: 'completed' | 'pending' | 'failed';
  created_at: string;
}

export interface BillingStoreBalance {
  store_id: string;
  store_name: string;
  main_balance: number;
}

export interface BillingPlanSummary {
  /** Slug paket. Untuk dibandingkan di kode — JANGAN dirender sebagai label. */
  tier: string;
  /**
   * Nama paket yang dibaca pelanggan, dari `plans.name`.
   *
   * Sudah dikirim backend sejak BILL-01B (`dashboard.go:365`); BILL-03 yang
   * menambahkan pembacanya. Bisa kosong untuk store tanpa paket — itu
   * keadaan sah, dan artinya tidak ada nama yang ditampilkan.
   */
  plan_name?: string;
  /**
   * Periode paket berjalan (BILL-01B). ISO-8601.
   *
   * Backend MENGHILANGKAN keduanya bila store belum punya periode — store
   * trial yang belum pernah membayar. Opsional di sini karena itu, bukan
   * karena backend lama: "belum ada periode" adalah keadaan sah, bukan
   * kegagalan membaca.
   */
  plan_started_at?: string;
  plan_expires_at?: string;
  quota_limits?: {
    whatsapp_limit: number;
    telegram_limit: number;
    livechat_limit: number;
    instagram_limit: number;
    api_limit?: number;
    whatsapp_conversations_limit?: number;
    telegram_conversations_limit?: number;
    instagram_conversations_limit?: number;
    livechat_conversations_limit?: number;
    api_conversations_limit?: number;
    ai_tokens_limit: number;
  };
  quota_used?: {
    whatsapp_used: number;
    telegram_used: number;
    livechat_used: number;
    instagram_used: number;
    api_used?: number;
    whatsapp_conversations_used?: number;
    telegram_conversations_used?: number;
    instagram_conversations_used?: number;
    livechat_conversations_used?: number;
    api_conversations_used?: number;
    ai_tokens_used: number;
  };
}

export interface BillingSummaryData {
  store_id: string;
  main_balance: number;
  transactions: BillingTransaction[];
  spending_total: number;
  spending_by_type: BillingTypeAmount[];
  store_balances: BillingStoreBalance[];
  plan_summary: BillingPlanSummary;
  overage_rates: Array<Record<string, unknown>>;
  payment_methods: Array<Record<string, unknown>>;
  invoices: BillingInvoice[];
}

export interface BillingTransaction {
  id: string;
  store_id: string;
  type: 'topup' | 'transfer_in' | 'transfer_out' | 'subscription' | 'overage' | 'refund';
  amount: number;
  description: string;
  status: 'completed' | 'pending' | 'failed';
  reference?: string;
  created_at: string;
}

export interface QuotaUsage {
  store_id: string;
  period_start: string;
  period_end: string;
  whatsapp_used: number;
  whatsapp_limit: number;
  telegram_used: number;
  telegram_limit: number;
  livechat_used: number;
  livechat_limit: number;
  instagram_used: number;
  instagram_limit: number;
  /** REST/chat API traffic (distinct from website widget livechat) */
  api_used?: number;
  api_limit?: number;
  whatsapp_conversations_used?: number;
  whatsapp_conversations_limit?: number;
  telegram_conversations_used?: number;
  telegram_conversations_limit?: number;
  instagram_conversations_used?: number;
  instagram_conversations_limit?: number;
  livechat_conversations_used?: number;
  livechat_conversations_limit?: number;
  api_conversations_used?: number;
  api_conversations_limit?: number;
  ai_tokens_used: number;
  ai_tokens_billed?: number;
  ai_tokens_limit: number;
  updated_at: string;
  /** Kurs USD→IDR dari DB (untuk tampilan biaya AI) */
  usd_to_idr: number;
}

export interface StorageUsage {
  total_bytes: number;
  total_gb: number;
  limit_gb: number;
  images_count: number;
  videos_count: number;
  documents_count: number;
  images_bytes: number;
  videos_bytes: number;
  documents_bytes: number;
}

export interface Notification {
  id: string;
  store_id: string;
  type: 'warning' | 'error' | 'critical' | 'info';
  title: string;
  message: string;
  action_label?: string;
  action_href?: string;
  is_read: boolean;
  created_at: string;
}

export interface AIInsights {
  conversations_handled: number;
  avg_response_time: number;
  cost_this_month: number;
  /** Billed cost (USD) = cost_this_month × (ai_tokens_billed / ai_tokens_used); use for display. */
  billed_cost_this_month?: number;
  ai_tokens_used?: number;
  ai_tokens_billed?: number;
  satisfaction_score: number;
  /** Kurs USD→IDR dari DB (untuk tampilan biaya AI) */
  usd_to_idr: number;
}

/**
 * Get dashboard stats for a time range (real data from API).
 * GET /api/v1/dashboard/stats?store_id=xxx&range=today|yesterday|7|14|30|90
 * Today/yesterday use Asia/Jakarta. Other ranges are rolling N days. Today is not cached.
 */
export async function getDashboardStats(
  storeId: string,
  range: DashboardStatsRange = 'today'
): Promise<DashboardStats> {
  try {
    const res = await httpClient.get<DashboardStats>(
      `/api/v1/dashboard/stats?store_id=${encodeURIComponent(storeId)}&range=${encodeURIComponent(range)}`
    );
    if (res.success && res.data) {
      return res.data;
    }
    throw new ApiError(res.error || 'Failed to load dashboard stats', 0);
  } catch (error) {
    throw error;
  }
}

/**
 * Get billing data
 * GET /api/v1/dashboard/billing?store_id=xxx
 */
export async function getBilling(storeId: string): Promise<BillingData> {
  try {
    const res = await httpClient.get<BillingData>(`/api/v1/dashboard/billing?store_id=${storeId}`);
    if (res.success && res.data) {
      return res.data;
    }
    throw new ApiError(res.error || 'Failed to load billing', 0);
  } catch (error) {
    throw error;
  }
}

/**
 * Get billing summary data for billing page (no mock fallback).
 * GET /api/v1/dashboard/billing/summary?store_id=xxx
 */
export async function getBillingSummary(storeId: string): Promise<BillingSummaryData> {
  try {
    const res = await httpClient.get<BillingSummaryData>(`/api/v1/dashboard/billing/summary?store_id=${encodeURIComponent(storeId)}`);
    if (res.success && res.data) {
      return res.data;
    }
    throw new ApiError(res.error || 'Failed to load billing summary', 0);
  } catch (error) {
    throw error;
  }
}

/**
 * Get quota usage
 * GET /api/v1/dashboard/quota?store_id=xxx
 */
export async function getQuota(storeId: string): Promise<QuotaUsage> {
  try {
    const res = await httpClient.get<QuotaUsage>(`/api/v1/dashboard/quota?store_id=${storeId}`);
    if (res.success && res.data) {
      return res.data;
    }
    throw new ApiError(res.error || 'Failed to load quota', 0);
  } catch (error) {
    throw error;
  }
}

/**
 * Get storage usage
 * GET /api/v1/dashboard/storage?store_id=xxx
 */
export async function getStorage(storeId: string): Promise<StorageUsage> {
  try {
    const res = await httpClient.get<StorageUsage>(`/api/v1/dashboard/storage?store_id=${storeId}`);
    if (res.success && res.data) {
      return res.data;
    }
    throw new ApiError(res.error || 'Failed to load storage', 0);
  } catch (error) {
    throw error;
  }
}

/**
 * Get notifications
 * GET /api/v1/dashboard/notifications?store_id=xxx&unread_only=true
 */
export async function getNotifications(storeId: string, unreadOnly: boolean = false): Promise<Notification[]> {
  try {
    const url = `/api/v1/dashboard/notifications?store_id=${storeId}${unreadOnly ? '&unread_only=true' : ''}`;
    const res = await httpClient.get<Notification[]>(url);
    if (res.success) {
      // Handle null response - return empty array
      return res.data || [];
    }
    throw new ApiError(res.error || 'Failed to load notifications', 0);
  } catch (error) {
    throw error;
  }
}

/**
 * Mark a notification as read
 * PATCH /api/v1/dashboard/notifications/:id/read
 */
export async function markNotificationAsRead(notificationId: string): Promise<void> {
  const res = await httpClient.patch<unknown>(`/api/v1/dashboard/notifications/${notificationId}/read`);
  if (!res.success) {
    throw new ApiError(res.error || 'Failed to mark notification as read', 0);
  }
}

/**
 * URL for SSE stream of new notifications (for EventSource).
 * Pass store_id and token in query (EventSource cannot send headers).
 */
export function getNotificationsStreamUrl(storeId: string): string {
  const base = getApiBaseUrl();
  const token = getAuthToken() ?? '';
  const params = new URLSearchParams({ store_id: storeId, token });
  return `${base}/api/v1/dashboard/notifications/stream?${params.toString()}`;
}

/**
 * Get AI insights
 * GET /api/v1/dashboard/ai-insights?store_id=xxx
 */
export async function getAIInsights(storeId: string): Promise<AIInsights> {
  try {
    const res = await httpClient.get<AIInsights>(`/api/v1/dashboard/ai-insights?store_id=${storeId}`);
    if (res.success && res.data) {
      return res.data;
    }
    throw new ApiError(res.error || 'Failed to load AI insights', 0);
  } catch (error) {
    throw error;
  }
}

/** Assignable agent for store (cs_agent / admin) */
export interface AssignableAgent {
  id: string;
  email: string;
  full_name: string;
  role: string;
  avatar: string;
}

/**
 * Get assignable agents for a store (for handover / team list)
 * GET /api/v1/agents?store_id=xxx
 */
export async function getAgents(storeId: string): Promise<AssignableAgent[]> {
  try {
    const res = await httpClient.get<{ agents: AssignableAgent[] }>(`/api/v1/agents?store_id=${encodeURIComponent(storeId)}`);
    if (res.success && res.data?.agents) {
      return res.data.agents;
    }
    return [];
  } catch (error) {
    console.error('[getAgents]', error);
    return [];
  }
}
